import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";

import ImageUpload from "@/components/image-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useUpdateUser from "@/graphql/mutations/use-update-user";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { type UserType, user } from "@/store";

function GroupInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      aria-autocomplete="none"
      autoComplete="off"
      autoCorrect="off"
      className={cn(
        "h-12.5 rounded-[10px] border-transparent bg-transparent text-16 text-white transition-colors placeholder:text-white/40 focus-visible:border-azure md:text-14",
        className
      )}
      spellCheck="false"
      {...props}
    />
  );
}

interface DetailsProps {
  onComplete(user: UserType): void;
}

export default function Details({ onComplete }: DetailsProps) {
  const [form, setForm] = useState({
    firstName: "",
    image: "",
    lastName: "",
  });
  const [isInProgress, setIsInProgress] = useState(false);

  const { mutationContext } = useAuth();
  const [updateUser, { data }] = useUpdateUser();

  const handleImage = (image: string) => setForm({ ...form, image });

  const handleInput = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsInProgress(true);
    updateUser(mutationContext({ input: form }));
  };

  useEffect(() => {
    if (data) {
      setIsInProgress(false);

      const { errors, user: updatedUser } = data.updateUser;

      if (!(errors || []).length && updatedUser) {
        user(updatedUser);
        onComplete(updatedUser);
      }
    }
  }, [data, onComplete]);

  return (
    <form
      autoComplete="off"
      autoCorrect="off"
      onSubmit={handleSubmit}
      spellCheck="false"
    >
      <div className="flex flex-col items-stretch gap-4">
        <p className="text-13/[1.5em] text-white">
          Adding a picture will really bring your profile to life.
        </p>
        <ImageUpload
          className="mx-auto"
          onChange={handleImage}
          onProgress={setIsInProgress}
        />
        <p className="text-13/[1.5em] text-white">
          Adding your real name to your profile makes it easier for your friends
          to follow you.
        </p>
        <div
          className={cn(
            "relative rounded-[10px] border border-white/20 bg-eerie-black",
            'after:absolute after:inset-x-0 after:top-1/2 after:h-px after:bg-white/20 after:transition-colors after:content-[""] has-focus-visible:after:bg-transparent'
          )}
        >
          <GroupInput
            name="firstName"
            onChange={handleInput}
            placeholder="First name"
            value={form.firstName}
          />
          <GroupInput
            name="lastName"
            onChange={handleInput}
            placeholder="Last name"
            value={form.lastName}
          />
        </div>
        <Button
          className="h-12.5 rounded-[25px]"
          disabled={isInProgress}
          type="submit"
          variant="cta"
        >
          {isInProgress ? (
            <Loader2Icon className="animate-spin" />
          ) : (
            "Complete profile"
          )}
        </Button>
      </div>
    </form>
  );
}
